import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import CommonSelect from "../common/commonSelect";
import { API } from "../../common/Constants";
import useFetch from "../../hooks/useFetch";
import { getAPIMultiPartHeaders } from "../../common/Utils";
import { PRIORITY_OPTIONS } from "./ticketListModal";

// Define props interface for the component
interface TicketGridModalProps {
  onTicketCreated?: () => void; // Optional callback when ticket is created
}

const TicketGridModal: React.FC<TicketGridModalProps> = ({
  onTicketCreated,
}) => {
  const priority = PRIORITY_OPTIONS;

  // Form state
  const [title, setTitle] = useState<string>("IT Support");
  const [subject, setSubject] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedPriority, setSelectedPriority] = useState(priority[0]);
  const [files, setFiles] = useState<FileList | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { callFetch } = useFetch();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 2) {
      setErrorMessage("You can only upload a maximum of 2 files");
      e.target.value = "";
      return;
    }
    setFiles(selectedFiles);
    setErrorMessage("");
  };

  const removeFiles = () => {
    setFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    setTitle("IT Support");
    setSubject("");
    setDescription("");
    setSelectedPriority(priority[0]);
    setFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setErrorMessage("");
  };

  // Handle priority change
  const handlePriorityChange = (value: string) => {
    const selectedOption =
      priority.find((opt) => opt.value === value) || priority[0];
    setSelectedPriority(selectedOption);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !title ||
      !subject ||
      !description ||
      selectedPriority.value === "Select"
    ) {
      setErrorMessage("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const formData = new FormData();

      // Create the request data
      const requestData = {
        title: title,
        subject: subject,
        description: description,
        priority: selectedPriority.value,
      };

      //Add the JSON request data as a blob
      formData.append(
        "requestData",
        new Blob([JSON.stringify(requestData)], {
          type: "application/json",
        })
      );

      // Add files if they exist
      if (files) {
        for (let i = 0; i < files.length; i++) {
          formData.append("screenshots", files[i]);
        }
      }

      // Make the API call
      await callFetch(
        API.CREATE_SUPPORT_TICKET,
        "POST",
        getAPIMultiPartHeaders(),
        null,
        formData
      );

      setSuccessMessage("Ticket created successfully!");
      resetForm();

      // Trigger the onTicketCreated callback if provided
      if (onTicketCreated) {
        onTicketCreated();
      }

      // Auto close the modal after 2 seconds
      setTimeout(() => {
        const closeButton = document.querySelector(
          '#add_ticket [data-bs-dismiss="modal"]'
        ) as HTMLElement;
        if (closeButton) {
          closeButton.click();
        }
        setSuccessMessage("");
      }, 2000);
    } catch (error) {
      console.error("Error creating ticket:", error);
      setErrorMessage("Failed to create ticket. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Add Ticket */}
      <div className="modal fade" id="add_ticket">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Ticket</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {successMessage && (
                  <div className="alert alert-success" role="alert">
                    {successMessage}
                  </div>
                )}
                {errorMessage && (
                  <div className="alert alert-danger" role="alert">
                    {errorMessage}
                  </div>
                )}
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        readOnly
                        disabled
                        style={{ backgroundColor: "#f8f9fa" }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Subject <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Ticket Description{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Priority <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        className="select"
                        options={priority}
                        defaultValue={selectedPriority}
                        onChange={handlePriorityChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Attach Screenshots/Documents (Max 2)
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={handleFileChange}
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                        ref={fileInputRef}
                      />
                      {files && files.length > 0 && (
                        <div className="mt-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <span>{files.length} file(s) selected</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-light"
                              onClick={removeFiles}
                            >
                              Clear
                            </button>
                          </div>
                          <ul className="list-unstyled mt-1">
                            {Array.from(files).map((file, index) => (
                              <li key={index}>
                                <i className="ti ti-file me-1"></i>
                                {file.name} ({(file.size / 1024).toFixed(2)} KB)
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Add Ticket"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketGridModal;
